import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Col, UiCenter, UiIcon, UiImage, UiPrimaryButton, UiText, useBreakpointValue } from "~/components/elements";
import { PageLayout } from "~/components/layouts/PageLayout";
import { appConfig } from "~/config";
import { PAGES } from "~/constants";
import { DASHBOARD_PARAMS } from "~/constants/dashboard";
import { useAppDispatch, useAppSelector } from "~/hooks";
import { confirmPayment } from "~/redux/dashboards/thunk";
import { DashboardMainBox } from "~/components/shared/DashboardMainBox";
import { MdArrowForwardIos } from "react-icons/md";
import paymentSuccess from "~/assets/img/v2/packages/payment_success.png";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { PAYMENT_TYPE_JOB } from "~/constants/payment";
import { useSearchParams } from "~/utils/helpers/react-router-helper";

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[];
};

declare const window: WindowWithDataLayer;

export default function PaymentSuccessfulPage() {
  const sendDataToGTM = useGTMDispatch();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const packageKey = searchParams.get("package");
  const jobId = searchParams.get("job_id") || undefined;
  const billingType = searchParams.get("billing_type") || undefined;
  const sessionId = searchParams.get(DASHBOARD_PARAMS.SESSION_ID);

  const vrTitle = useBreakpointValue<any>({ base: "headline3", sm: "headline2" });
  const currentUser = useAppSelector(state => state.auth.currentUser);

  useEffect(() => {
    const handleConfirmPayment = async () => {
      if (sessionId) {
        const type = billingType || PAYMENT_TYPE_JOB;
        await dispatch(confirmPayment({ data: { sessionId, type, jobId } }));
        searchParams.delete(DASHBOARD_PARAMS.SESSION_ID);
        history.replace({
          search: searchParams.toString(),
        });
        // window.location.reload();
      }
    };
    handleConfirmPayment();
  }, [billingType, dispatch, history, jobId, packageKey, searchParams, sessionId]);

  useEffect(() => {
    if (appConfig.stage === "production") {
      sendDataToGTM({
        event: "pageview",
        page: {
          title: "payment-successful",
        },
      });
    }
  }, [sendDataToGTM]);

  useEffect(() => {
    if (window.rewardful && currentUser?.email) {
      window.rewardful("convert", { email: currentUser.email });
    }
  }, [currentUser]);

  const handleNavigateToDashboard = () => {
    if (billingType === "upsell") {
      window.location.href = `${PAGES.SELECT_STYLES}?type=upsell&job_id=${jobId}`;
      return;
    }
    if (billingType === "job") {
      window.location.href = PAGES.SELECT_PACKAGES;
      return;
    }
    window.location.href = PAGES.VIEW_RESULTS;
  };

  return (
    <PageLayout
      metaTagsConfig={{
        title: `Bettershot`,
        metas: [{ name: "robots", content: "noindex,nofollow" }],
      }}
      as={Col}
      flex={1}
    >
      <Col flex={1} justify="center">
        <UiCenter p={4}>
          <DashboardMainBox>
            <UiCenter flexDirection="column">
              <UiImage src={paymentSuccess} w="5rem" />
              <UiText vr={vrTitle} textAlign="center" mt={3}>
                Hooray! Your payment was confirmed
              </UiText>
              <UiText vr="body1" textAlign="center" mt={3}>
                Thank you for choosing Bettershot.
                <br />
                You will receive an automated email receipt shortly.
              </UiText>

              <UiPrimaryButton
                mt="8"
                fontSize={{ base: "0.875rem", sm: "1rem" }}
                whiteSpace="normal"
                lineHeight="20px"
                h="auto"
                py={3}
                onClick={handleNavigateToDashboard}
                w="full"
              >
                Continue creating professional photoshoots
                <UiIcon hideBelow="sm" as={MdArrowForwardIos} boxSize={5} color="white" />
              </UiPrimaryButton>
            </UiCenter>
          </DashboardMainBox>
        </UiCenter>
      </Col>
    </PageLayout>
  );
}
